import { Button, Job } from '@cibli/shared';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { JobCard } from '@/components/molecules/JobCard';

import { useFilteredJobsContext } from '../contexts/FilteredJobsContext';

interface JobListColumnProps {
  selectedJob: Job | null;
  onJobClick: (job: Job) => void;
}

export default function JobListColumn({ selectedJob, onJobClick }: JobListColumnProps) {
  const { t } = useTranslation();
  const { jobs, pagination, handleLoadMore, hasAppliedToJob, hasSourceError } =
    useFilteredJobsContext();

  return (
    <div className="relative flex-1 overflow-y-auto pr-1 scrollbar-hide">
      {jobs.map(job => (
        <JobCard
          key={job.id}
          className={`mb-4 transition-all duration-300 m-1 ${
            selectedJob?.id === job.id ? 'ring-2 ring-[#10b981] rounded-xl' : ''
          }`}
          job={job}
          onClick={() => onJobClick(job)}
          showExperience={true}
          showPublicationDate={true}
          hasApplied={hasAppliedToJob({ id: job.id })}
        />
      ))}

      {hasSourceError && (
        <div className="flex items-center gap-2 rounded-md border border-yellow-300 bg-yellow-50 px-4 py-3 text-sm text-yellow-800 mb-4">
          <AlertTriangle className="h-4 w-4 flex-shrink-0" />
          {t('MatchingJobs.partialResults')}
        </div>
      )}

      {pagination.hasMore && (
        <div className="flex justify-center pt-4">
          <Button
            onClick={handleLoadMore}
            disabled={pagination.isLoading}
            variant="outline"
            aria-label={t('dashboard.jobs.loadMore')}
          >
            {pagination.isLoading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {t('dashboard.jobs.loadingMore')}
              </>
            ) : (
              t('dashboard.jobs.loadMore')
            )}
          </Button>
        </div>
      )}

      {!pagination.hasMore && jobs.length > 0 && (
        <p className="text-center text-sm text-muted-foreground py-4">
          {pagination.hasActiveFilters
            ? t('dashboard.jobs.filteredResults', { count: pagination.total })
            : t('dashboard.jobs.allLoaded', { count: pagination.total })}
        </p>
      )}
    </div>
  );
}
